import { Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import { AnimatedNumber } from '../../../../common/components/AnimatedNumber';
import { DataChip } from '../../../../common/components/DataChip';
import { SEVERITY_COLORS, Severity } from '../../../../common/enums/Severity';
import { useTabbable } from '../../../../common/hooks/useTabbable';

type SeverityDataProps = {
  high: number;
  medium: number;
  low: number;
  passed: number;
  onElementSelect: (idx?: number) => void;
  activeIndex?: number;
  showPassed?: boolean;
};

export const SeverityData = ({ high, medium, low, passed, onElementSelect, activeIndex, showPassed = true }: SeverityDataProps) => {
  const [ref, onKeyDown] = useTabbable({ direction: 'vertical', selector: '[data-role="severity-data-entry"]' });

  const total = high + medium + low + passed;

  const rows = [
    {
      name: t('high'),
      amount: high,
      percentage: total === 0 ? 0 : (high / total) * 100,
      ...SEVERITY_COLORS[Severity.HIGH],
    },
    {
      name: t('medium'),
      amount: medium,
      percentage: total === 0 ? 0 : (medium / total) * 100,
      ...SEVERITY_COLORS[Severity.MED],
    },
    {
      name: t('low'),
      amount: low,
      percentage: total === 0 ? 0 : (low / total) * 100,
      ...SEVERITY_COLORS[Severity.LOW],
    },
  ];

  if (showPassed) {
    rows.push({
      name: t('passed'),
      amount: passed,
      percentage: total === 0 ? 0 : (passed / total) * 100,
      ...SEVERITY_COLORS[Severity.PASSED],
    });
  }

  return (
    <Stack direction="column" alignItems="stretch" spacing={1} sx={{ py: 1, width: '100%' }} ref={ref} onKeyDown={onKeyDown}>
      {rows.map(({ name, amount, percentage, main, background, text }, idx) => (
        <Stack
          key={idx}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: 1,
            py: 0.625,
            borderRadius: '4px',
            transition: 'all 0.3s ease-in-out',
            backgroundColor: activeIndex === idx ? 'backdrops.grey' : '',
            '&:focus-visible': { backgroundColor: 'backdrops.grey' },
          }}
          role="presentation"
          tabIndex={idx === 0 ? 0 : -1}
          data-role="severity-data-entry"
          spacing={2}
          onClick={() => onElementSelect(idx)}
          onMouseEnter={() => onElementSelect(idx)}
          onMouseLeave={() => onElementSelect()}
        >
          <Stack direction="column">
            <Typography variant="subtitle1" component={'div'} sx={{ lineHeight: 1 }}>
              {name}
            </Typography>
            <Typography
              variant="subtitle1"
              component={'div'}
              sx={{
                color: 'text.secondary',
                mt: 0.125,
                lineHeight: 1.25,
              }}
            >
              <AnimatedNumber value={percentage} suffix={'%'} decimals={2} />
            </Typography>
          </Stack>
          <DataChip textColor={text} backgroundColor={background} borderColor={main} label={<AnimatedNumber value={amount} />} />
        </Stack>
      ))}
    </Stack>
  );
};
